import type { AspectRatioToken } from '@/lib/platform-matrix/placement-matrix';

export interface StageSize {
  width: number;
  height: number;
}

/**
 * Native authoring pixel size per ratio — the denominator `fontScale` is
 * measured against.
 *
 * These are the real platform delivery sizes, not a normalised 1080-wide family,
 * and that matters: if every ratio were 1080 wide then re-materialising
 * `fontSize` would be a no-op and the §7.3a rule would be decorative. The
 * portrait family genuinely IS 1080 wide (1:1, 4:5, 9:16, 3:4), so a headline
 * keeps its pixel size across them — correct, because at equal width the same
 * pixel size covers the same fraction of the line. What changes is the available
 * VERTICAL band, which is exactly why every sibling is marked `needs_review`
 * rather than silently trusted.
 *
 * `1080` matches `SYNTH_STAGE_WIDTH` in `build-overlay-layers.ts` and
 * `LEGACY_STAGE_WIDTH` in `overlay-svg.ts`, so layers authored here composite at
 * the right scale through the existing server-side renderer.
 */
export const RATIO_STAGE: Record<AspectRatioToken, StageSize> = {
  '1:1': { width: 1080, height: 1080 },
  '4:5': { width: 1080, height: 1350 },
  '9:16': { width: 1080, height: 1920 },
  '3:4': { width: 1080, height: 1440 },
  '2:3': { width: 1000, height: 1500 },
  '16:9': { width: 1920, height: 1080 },
  '1.91:1': { width: 1200, height: 628 },
};

export function stageFor(ratio: AspectRatioToken): StageSize {
  return RATIO_STAGE[ratio];
}

/** Materialise a dimensionless font scale into pixels for one ratio (§7.3a). */
export function materialiseFontSize(fontScale: number, ratio: AspectRatioToken): number {
  return Math.round(fontScale * RATIO_STAGE[ratio].width);
}

/** The inverse: the Size field writes `fontScale`, never a raw pixel size. */
export function fontScaleFromSize(fontSize: number, ratio: AspectRatioToken): number {
  return fontSize / RATIO_STAGE[ratio].width;
}
